import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import { ContainedButton } from 'components/ContainedButton';
import { TextButton } from 'components/TextButton';
import { OrderMember } from 'graphql/wire-transfers/queries';
import { TFunction } from 'i18next';
import React, { FC, useEffect, useState } from 'react';
import { formatMoney } from 'utils/helpers';

import { InstructionsRequest } from '../../types';
import SearchAuthorization from '../searchAuth';
import { useStyles } from './styles';
import { AccountingType } from 'v2-types/order';
import SelectField from 'components/select-field';
import { wireDataListType } from '../../wire-transfers-hooks';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { schema } from './schemaForm';

type ModalCreateWireProps = {
	t: TFunction;
	open: boolean;
	handleToggleModals: () => void;
	members: OrderMember[];
	handleCreateWire: (authorization: string[], accounting: InstructionsRequest[]) => void;
	disbursments: AccountingType[];
	selecteDisbursments: string;
	handleChangeDisbursements: (disbursement: any) => void;
	wireDataList: wireDataListType[];
};

type Instruccion = {
	accounting_id: string;
	amount: number;
	association_id: string;
	payment_id: string;
};

type FormValues = {
	instructions: Instruccion[];
	authorization: string[];
};

export const ModalCreateWire: FC<ModalCreateWireProps> = ({
  t,
  open,
  handleToggleModals,
  members,
  handleCreateWire,
  disbursments,
  selecteDisbursments,
  handleChangeDisbursements,
  wireDataList,
}) => {
  const classes = useStyles();
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { isValid },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      instructions: [],
      authorization: [],
    },
  });

  const transfers = disbursments.find((disb) => disb._id === selecteDisbursments);

  useEffect(() => {
    handleChangeDisbursements(null);
    // Mount-only reset; re-running on every render would clobber the parent's selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (transfers?.accounted_for) {
      const initialInstructions = transfers.accounted_for.map(() => ({
        accounting_id: '',
        amount: 0,
        association_id: '',
        payment_id: '',
      }));

      setValue('instructions', initialInstructions);
    }
  }, [transfers, setValue]);

  return (
    <Dialog open={open} onClose={() => handleToggleModals()} fullWidth maxWidth="lg">
      <DialogTitle className={classes.dialgoTitleContainer}>
        <Grid container direction="column">
          <Grid item className={classes.closeIconContainer}>
            <Button onClick={() => handleToggleModals()} style={{ minWidth: 'auto' }}>
              <FontAwesomeIcon
                icon="plus"
                size="lg"
                transform={{ rotate: 45 }}
                className={classes.closeIcon}
              />
            </Button>
          </Grid>
          <Grid item>
            <Typography variant="h6" className={classes.dialogTitle}>
              {t('payments:create-wire-tranfer')}
            </Typography>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} direction="column">
          <Grid item>
            <Typography variant="body1" align="center">
              {t('payments:create-wire-transfer-advice')}
            </Typography>
          </Grid>
          <Grid item>
            <Controller
              name="authorization"
              control={control}
              render={({ field }) => (
                <SearchAuthorization
                  members={members}
                  selectedMembers={field.value}
                  handledSelectAuth={(newAuth) => {
									  if (!field.value.includes(newAuth)) {
									    field.onChange([...field.value, newAuth]);
									  }
                  }}
                  t={t}
                />
              )}
            />
          </Grid>
          <Grid item container spacing={2}>
            {(watch('authorization') || []).map((auth) => (
              <Grid item key={auth}>
                <Chip
                  color="secondary"
                  style={{ marginRight: '4px' }}
                  label={auth}
                  onDelete={() => {
									  const updated = watch('authorization').filter((a) => a !== auth);
									  setValue('authorization', updated);
									  trigger('authorization');
                  }}
                />
              </Grid>
            ))}
          </Grid>
          <Grid item className={classes.gridTransfers}>
            <SelectField
              value={selecteDisbursments}
              data={disbursments.map(d => ({ ...d, code: d._id }))}
              dataKey="_id"
              dataValue="_id"
              dataText="description"
              label={t('payments:disbursements')}
              handleChange={(e) => {
							  handleChangeDisbursements(e.target.value);
							  setValue('instructions', []);
              }}
            />
          </Grid>
          {selecteDisbursments && (
          <>
            <Grid item className={classes.gridTransfers}>
              <Typography>{t('payments:transfers')}</Typography>
              <Box className={classes.transferList}>
                {transfers?.accounted_for?.map((trans, index) => {
									  const { record } = trans;
									  const { letter, number, _id, description } = record;
									  const {
									    _id: idInvolvedParties,
									    amount = 0,
									    name = '',
									    order_kinds = [],
									  } = trans.record?.involved_parties?.[0] || {};
									  const orderKinds = order_kinds.sort((a, b) => a.localeCompare(b));

									  return (
  <Box className={classes.transfersContainer} key={_id}>
    <Box className={classes.transContainer}>
      <Box>
        <Typography>
          {description}
          {letter && number && ` (${letter}-${number})`}
        </Typography>
        <Typography>
          {name}
          {` (${orderKinds.join(', ')})`}
        </Typography>
      </Box>
      <Box>
        <Typography>{formatMoney(amount)}</Typography>
      </Box>
    </Box>
    <Box className={classes.selecTransfer}>
      <Controller
        name={`instructions.${index}`}
        control={control}
        render={({ field }) => (
          <SelectField
            label={t('payments:accounts')}
            data={
																	wireDataList.find((wires) => wires.data_id === idInvolvedParties)
																	  ?.wire ?? []
																}
            dataKey="_id"
            dataValue="_id"
            dataText="description"
            value={field.value?.payment_id || ''}
            handleChange={(e) => {
																  field.onChange({
																    accounting_id: _id,
																    amount,
																    association_id: idInvolvedParties,
																    payment_id: e.target.value,
																  });
            }}
          />
        )}
      />
    </Box>
  </Box>
									  );
                })}
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.totalContainer}>
                <Typography>{t('payments:total')}</Typography>
                <Typography>
                  {formatMoney(
										  (watch('instructions') || []).reduce(
										    (sum, item) => sum + (item?.amount ?? 0),
										    0
										  )
                  )}
                </Typography>
              </Box>
            </Grid>
          </>
          )}
        </Grid>
        <Box className={classes.actionsContainer}>
          <TextButton text={t('common:cancel')} onClick={() => handleToggleModals()} />
          <ContainedButton
            text={t('common:create')}
            disabled={!isValid}
            onClick={handleSubmit((data) => {
						  handleCreateWire(data.authorization, data.instructions);
						  handleToggleModals();
            })}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};
